import { useLayoutEffect, useRef } from 'react';

const FOCUS_TRAP_WARNING_MESSAGE =
  '[useFocusTrap] No focusable elements found in the container.';

const focusableSelectors = `
  button, [href], input, select, textarea, summary,
  [tabindex]:not([tabindex="-1"]), [contenteditable="true"]
`;

export function useFocusTrap<T extends HTMLElement>(
  active: boolean | undefined,
  options?: {
    onFocus?: (container: T) => void;
  },
) {
  const containerRef = useRef<T | null>(null);
  const previouslyFocusedElement = useRef<Element | null>(null);

  const handleKeyDown = (event: KeyboardEvent) => {
    const container = containerRef.current as T;

    const focusableElements = getFocusableElements(container);

    if (event.key !== 'Tab') {
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  const getFocusableElements = (container: T) => {
    return Array.from(
      container.querySelectorAll<HTMLElement>(focusableSelectors),
    ).filter(element => {
      const computedStyle = getComputedStyle(element);
      return (
        (element !== container || container.hasAttribute('tabindex')) &&
        computedStyle.display !== 'none' &&
        computedStyle.visibility !== 'hidden'
      );
    });
  };

  const focusOnFirstElement = (focusableElements: Array<HTMLElement>) => {
    focusableElements[0]?.focus();
  };

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!active || !container) {
      return;
    }

    previouslyFocusedElement.current = document.activeElement;

    if (options?.onFocus) {
      options.onFocus(container);
    } else {
      const focusableElements = getFocusableElements(container);
      const hasFocusableElements = focusableElements.length > 0;

      if (!hasFocusableElements) {
        if (
          process.env.NODE_ENV === 'development' ||
          process.env.NODE_ENV === 'test'
        ) {
          // eslint-disable-next-line no-console
          console.warn(FOCUS_TRAP_WARNING_MESSAGE);
        }
        return;
      }

      focusOnFirstElement(focusableElements);
    }

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      if (previouslyFocusedElement.current instanceof HTMLElement) {
        previouslyFocusedElement.current.focus();
      }
      container.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, options?.onFocus]);

  return { containerRef };
}
